import { useState, useEffect, useRef } from "react";
import {
    Card,
    CardBody,
    CardFooter,
    Checkbox,
    Button,
    Typography,
} from "@material-tailwind/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Image from "components/image";
import Trademark from "components/Trademark";
import MaterialIcon from "components/material-icon";
import FormikPasswordInput from "components/formik-password-input/formik-password-input";
import FormikInputIcon from "components/form/formik-input-icon";
import logo from "assets/img/logos/gobby-anim.gif";
import googlePlayBadge from "assets/img/GetItOnGooglePlay_Badge_Web_color_English.svg";
import { fetcherFull } from "utils/api";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router";
import { useRateLimit, sanitiseText } from "utils/utils.jsx";

export default function Register() {
    const [gobbyTermsChecked, setGobbyTermsChecked] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const route = searchParams.get('route');
    const { isBlocked: registerBlocked } = useRateLimit();
    const [registrationSubmitted, setRegistrationSubmitted] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [resendDisabled, setResendDisabled] = useState(false);
    const resendButtonRef = useRef(null);

    useEffect(() => {
        if (registrationSubmitted && resendButtonRef.current) {
            resendButtonRef.current.focus();
        }
    }, [registrationSubmitted]);

    const handleUserSubmit = async (values) => {
        if (registerBlocked) {
            toast.error('Too many attempts. Please try again later.');
            return;
        }

        let sanitary_values = {};
        for (const property in values) {
            if (values[property]) {
                sanitary_values[property] = values[property];
            }
        }

        if (sanitary_values.email) {
            const emailPrefix = sanitary_values.email.split("@")[0];
            // Default display name to email prefix if empty
            if (!sanitary_values.name) {
                sanitary_values.name = emailPrefix;
            }
            // Default organisation name to email address
            if (!sanitary_values.organisation_name) {
                sanitary_values.organisation_name = `${sanitary_values.email}'s Organisation`;
            }
        }

        // Sanitize name fields before submission
        if (sanitary_values.name) {
            sanitary_values.name = sanitiseText(sanitary_values.name);
        }
        if (sanitary_values.organisation_name) {
            sanitary_values.organisation_name = sanitiseText(sanitary_values.organisation_name);
        }
        // Password deliberately left verbatim: sanitiseInput strips <>"',
        // which silently rewrites the credential and locks out any password
        // set outside this app. See #1097 and gobby-app/gobbyapi#466.

        sanitary_values.frontend_url = window.location.origin;
        if (route) {
            sanitary_values.route = route;
        }

        const userSubmitToast = toast.loading("Creating User...");
        let response = undefined;
        let serverError = undefined;

        if (sanitary_values) {
            try {
                response = await fetcherFull(
                    `/v2/user`,
                    "POST",
                    sanitary_values,
                );
            } catch (error) {
                // Extract error message from the thrown Error
                // fetcherFull throws: "${errorJson.error} ${errorJson.message}"
                // Backend returns: { error: 'duplicate user found', message: 'Duplicate entry...' }
                const errorMessage = error?.message || "";
                const lowerErrorMsg = errorMessage.toLowerCase();

                // Check for duplicate user errors (422 from backend)
                if (lowerErrorMsg?.includes("duplicate user found") ||
                    lowerErrorMsg?.includes("duplicate entry")) {
                    // Parse the DBI error message to determine which field is duplicate
                    if (lowerErrorMsg.includes("for key") && lowerErrorMsg.includes(".email")) {
                        // SECURITY: Use generic message for email to prevent enumeration attacks
                        serverError = "Registration failed. Please try again or contact support.";
                    } else if (lowerErrorMsg.includes("for key") &&
                               (lowerErrorMsg.includes(".name") || lowerErrorMsg.includes("organisation"))) {
                        // Organization name conflicts are safe to reveal (not PII)
                        serverError = "This organization name is already in use";
                    } else {
                        // Unknown duplicate field - use generic message
                        serverError = "Registration failed. Please try again or contact support.";
                    }
                } else if (lowerErrorMsg?.includes("missing_field")) {
                    // Handle missing field errors - safe to show field names
                    serverError = errorMessage.replace(/MISSING_FIELD\s*/i, "").trim() || "Required field missing";
                } else {
                    // For other errors, use generic message to avoid leaking information
                    serverError = "Registration failed. Please try again or contact support.";
                }
            }
            if (response && response?.name) {
                toast.update(userSubmitToast, {
                    render: "Registration submitted. Please check your email for verification.",
                    type: "info",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
                setRegistrationSubmitted(true);
                setUserEmail(values.email);
            } else if (serverError) {
                toast.update(userSubmitToast, {
                    render: `Error creating User, ${
                        serverError || "unknown error"
                    }`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            } else {
                toast.update(userSubmitToast, {
                    render: `Error creating User, unknown error`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            }
        } else {
            toast.update(userSubmitToast, {
                render: `Error creating User`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };

    const handleResendLink = async () => {
        if (resendDisabled) return;
        setResendDisabled(true);
        const resendToast = toast.loading("Sending verification email...");
        try {
            const requestBody = {
                email: userEmail,
                frontend_url: window.location.origin
            };
            if (route) {
                requestBody.route = route;
            }
            const response = await fetcherFull('/v2/user/resend', 'POST', requestBody);
            if (response && response.success) {
                toast.update(resendToast, {
                    render: "Verification email sent again.",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                });
            } else {
                throw new Error("Failed to send email");
            }
        } catch (error) {
            toast.update(resendToast, {
                render: "Failed to send verification email. Please try again later.",
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });
        }
        setTimeout(() => setResendDisabled(false), 10000);
    };

    const initialCreateValues = {
        email: "",
        password: "",
        confirmPassword: "",
        organisation_name: "",
    };

    const validationSchemaCreate = Yup.object().shape({
        email: Yup.string().required("Please enter an email"),
        password: Yup.string()
            .min(8, "Password must be at least 8 characters")
            // See resetconfirm.js: verbatim passwords (#1097) mean this is the
            // only thing rejecting an all-whitespace credential.
            .matches(/\S/, "Password cannot be only spaces")
            .required("Please provide a password"),
        confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match').required('Please confirm your password'),
    });

    return (
        <>
            {!registrationSubmitted ? (
                <div className="flex flex-col min-w-screen min-h-screen bg-white">
                    <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                        <Image
                            className="sm:w-[250px] w-[0px]"
                            src={logo}
                            rounded={false}
                            raised={false}
                            alt={"Gobby Logo"}
                        />
                    </div>
                    <div className="flex min-h-fit justify-center">
                        <Card
                            className="card-inner filter-none sm:p-8 p-4"
                            shadow={false}
                        >
                            <Typography
                                variant="h3"
                                className="text-primary text-center lg:content-center "
                            >
                            Sign up to Start Using Gobby
                                <Trademark />
                            </Typography>
                            <Formik
                                initialValues={initialCreateValues}
                                onSubmit={handleUserSubmit}
                                validationSchema={validationSchemaCreate}
                            >
                                {({ isValid, dirty }) => (
                                    <Form>
                                        <CardBody className="card-inner-body font-body my-4 px-2 py-2">
                                            <div className="px-0 py-2">
                                                <Typography
                                                    as="div"
                                                    className="px-4 rounded-2xl md:mt-4 mt-2"
                                                >
                                                    <FormikInputIcon
                                                        name="email"
                                                        iconName="email"
                                                        label="Email"
                                                        autoComplete="username"
                                                    />
                                                </Typography>

                                                <Typography
                                                    as="div"
                                                    className="px-4 rounded-2xl md:mt-4 mt-2"
                                                >
                                                    <FormikPasswordInput
                                                        name="password"
                                                        label="Password"
                                                        showStrength={true}
                                                        autoComplete="new-password"
                                                    />
                                                </Typography>

                                                <Typography
                                                    as="div"
                                                    className="px-4 rounded-2xl md:mt-4 mt-2"
                                                >
                                                    <FormikPasswordInput
                                                        name="confirmPassword"
                                                        label="Confirm Password"
                                                        autoComplete="new-password"
                                                    />
                                                </Typography>

                                                <Typography
                                                    as="div"
                                                    className="px-4 rounded-2xl md:mt-4 mt-2"
                                                >
                                                    <FormikInputIcon
                                                        name="organisation_name"
                                                        iconName="business_center"
                                                        label="Organisation Name"
                                                    />
                                                </Typography>
                                            </div>
                                        </CardBody>
                                        <CardFooter className="p-0">
                                            <div className="flex flex-col items-center font-body gap-2">
                                                <Typography
                                                    variant="h4"
                                                    className="text-primary text-center md:text-xl lg:content-center "
                                                >
                                                Ready to be Gobby
                                                    <Trademark />?
                                                </Typography>
                                                <div className="flex flex-col items-center sm:text-lg text-sm text-primary">
                                                    <div className="flex items-center">
                                                        <Checkbox
                                                            className="checked:bg-primary checked:border-primary checked:before:bg-primary"
                                                            containerProps={{ className: "py-1" }}
                                                            onChange={() => {
                                                                setGobbyTermsChecked(
                                                                    (
                                                                        gobbyTermsChecked,
                                                                    ) =>
                                                                        !gobbyTermsChecked,
                                                                );
                                                            }}
                                                        />
                                                        <Typography as="span" variant="paragraph">
                                                    I have read & accept the&nbsp;
                                                            <a
                                                                style={{
                                                                    color: "revert",
                                                                }}
                                                                href={
                                                                    "https://gobby.io/saas-terms/"
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                        Gobby Terms & Conditions
                                                            </a>
                                                        </Typography>
                                                    </div>
                                                    <Typography as="span" variant="paragraph">
                                                        <a
                                                            style={{
                                                                color: "revert",
                                                            }}
                                                            href="https://gobby.io/privacy-policy/"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Click here to read our Privacy Policy
                                                        </a>
                                                    </Typography>
                                                </div>
                                            </div>
                                            <div className="flex justify-center">
                                                <Typography
                                                    variant="paragraph"
                                                    className="flex justify-center mt-2 mb-4 bg-bb font-body text-primary font-normal"
                                                >
                                                You will be sent an email to
                                                confirm your account, together
                                                with a link to the login page.
                                                </Typography>
                                            </div>
                                            <div className="flex justify-center">
                                                <Button
                                                    type="submit"
                                                    variant="filled"
                                                    size="lg"
                                                    className="btn btn--cta rounded-full h-24 w-24 font-display text-2xl"
                                                    disabled={
                                                        !(
                                                            isValid &&
                                                            dirty &&
                                                            gobbyTermsChecked &&
                                                            !registerBlocked
                                                        )
                                                    }
                                                >
                                                Go
                                                </Button>
                                            </div>
                                        </CardFooter>
                                    </Form>
                                )}
                            </Formik>
                        </Card>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col min-w-screen min-h-screen bg-white">
                    <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                        <Image
                            className="sm:w-[250px] w-[0px]"
                            src={logo}
                            rounded={false}
                            raised={false}
                            alt={"Gobby Logo"}
                        />
                    </div>
                    <div className="flex min-h-fit justify-center">
                        <Card
                            className="card-inner filter-none sm:p-8 p-4"
                            shadow={false}
                        >
                            <Typography
                                variant="h3"
                                className="text-primary text-center lg:content-center "
                            >
                                <MaterialIcon name="email" className="mr-2" />
                                Authentication Required
                            </Typography>
                            <CardBody className="card-inner-body font-body my-4 px-2 py-2">
                                <Typography variant="paragraph" className="text-center mb-4">
                                    For your security, we need to authenticate your request. We&apos;ve sent a
                                    verification link to {userEmail}. Please check your email and click the link to
                                    complete the account creation process.
                                </Typography>
                                <Typography variant="small" className="text-center text-black mb-4">
                                    Please allow a minimum of 5 minutes to receive your email, before trying again.
                                </Typography>
                                <div className="text-center mb-4">
                                    <Typography variant="paragraph">
                                        If you need an Authenticator app for future logins, download and install
                                        Google Authenticator using the links below:
                                    </Typography>
                                    <div className="flex flex-col sm:flex-row py-2 gap-2 justify-center items-center">
                                        <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer"><img src={googlePlayBadge} alt="Get it on Google Play" className="h-[7vh] w-auto" /></a>
                                        <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer"><img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" className="h-[7vh] w-auto" /></a>
                                    </div>
                                    <Typography variant="paragraph">
                                        Alternatively, search for &lsquo;Authenticator&rsquo; in your preferred app store.
                                    </Typography>
                                </div>
                            </CardBody>
                            <CardFooter className="p-0">
                                <div className="flex flex-col items-center font-body">
                                    <Button
                                        type="button"
                                        variant="filled"
                                        size="lg"
                                        className="btn btn--cta mb-4"
                                        disabled={resendDisabled}
                                        onClick={handleResendLink}
                                        ref={resendButtonRef}
                                        aria-label="Resend verification link"
                                    >
                                        {resendDisabled ? "Resend Disabled" : "Resend Link"}
                                    </Button>
                                    {/* #922 on FE GH */}
                                    {/* <Tooltip content="Close and Return to Login Page">
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            size="lg"
                                            className="btn btn--outlined"
                                            aria-label="Close and return to home"
                                            onClick={() => navigate('/') }
                                        >
                                            Login
                                        </Button>
                                    </Tooltip> */}
                                </div>
                            </CardFooter>
                        </Card>
                    </div>
                </div>
            )}
        </>);
}
